import { createFileFingerprintWithWorker } from '@lib/files/fingerprint';
import {
  generateLQIP,
  getImageThumbnail,
  getMediaInfo,
  getVideoThumbnail,
} from '@lib/utils/videoDuration';
import { useEffect, useState } from 'react';

export interface FileWithDuration extends File {
  duration?: number;
  thumbnail?: string;
  lqip?: string;
  width?: number;
  height?: number;
  fingerprint?: string;
  /** True once this file's metadata batch has finished — regardless of whether
   * fingerprinting succeeded. Drives the per-row "pending" skeleton, so a file
   * whose fingerprint failed still resolves (and is uploaded; it just can't be
   * dedup-checked). */
  metadataExtracted?: boolean;
}

/**
 * Fingerprint a file, retrying a few times before giving up. Returns undefined
 * on persistent failure (never throws) so the file is still included in the
 * upload — it just won't be duplicate-checked.
 */
async function fingerprintWithRetry(file: File, attempts = 3): Promise<string | undefined> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      return await createFileFingerprintWithWorker(file);
    } catch (error) {
      if (attempt === attempts - 1) {
        console.warn(
          `Fingerprint failed for "${file.name}" after ${attempts} attempts; including the file without duplicate detection:`,
          error instanceof Error ? error.message : error,
        );
        return undefined;
      }
    }
  }
  return undefined;
}

/**
 * Read the client-side thumbnail decorated onto a File by the metadata
 * extraction hook (or by the file-picker / drag-drop validators). Prefers the
 * full-quality `thumbnail` (data URL captured pre-upload) and falls back to
 * the lightweight `lqip`. Returns null when neither is present.
 */
export function getFileThumbnailUrl(file: File): string | null {
  const enriched = file as FileWithDuration;
  return enriched.thumbnail ?? enriched.lqip ?? null;
}

interface FileMetadataExtractionResult {
  filesWithMetadata: FileWithDuration[];
  filesWithFingerprints: Map<File, string>;
  isFingerprintingComplete: boolean;
}

/**
 * Extracts metadata (duration, thumbnail, dimensions, fingerprint, LQIP) from files
 * in batches. Batch size adapts to average file size to prevent browser overload.
 *
 * Returns enriched files, a fingerprint map, and a completion flag.
 */
export function useFileMetadataExtraction(files: File[]): FileMetadataExtractionResult {
  const [filesWithMetadata, setFilesWithMetadata] = useState<FileWithDuration[]>(files);
  const [filesWithFingerprints, setFilesWithFingerprints] = useState<Map<File, string>>(new Map());
  const [isFingerprintingComplete, setIsFingerprintingComplete] = useState(false);

  useEffect(() => {
    // No files — reset immediately
    if (files.length === 0) {
      setFilesWithMetadata([]);
      setFilesWithFingerprints(new Map());
      setIsFingerprintingComplete(false);
      return;
    }

    let cancelled = false;
    setIsFingerprintingComplete(false);
    // Expose the files immediately so the selection list can render right away
    // (with skeleton thumbnails). Metadata is mutated onto these same File
    // objects per batch below, and each batch bumps the array ref so the grid
    // re-reads the now-enriched files.
    setFilesWithMetadata(files);

    const enrichFilesWithMetadata = async () => {
      // Dynamic batch size based on average file size to prevent overwhelming the browser
      // Smaller files (< 50MB avg) can be processed in larger batches
      const LARGE_FILE_THRESHOLD_BYTES = 50_000_000; // 50MB
      const BATCH_SIZE_SMALL_FILES = 8;
      const BATCH_SIZE_LARGE_FILES = 5;

      const avgFileSize = files.reduce((sum, f) => sum + f.size, 0) / files.length;
      const BATCH_SIZE =
        avgFileSize < LARGE_FILE_THRESHOLD_BYTES ? BATCH_SIZE_SMALL_FILES : BATCH_SIZE_LARGE_FILES;
      const allEnrichedFiles: FileWithDuration[] = [];

      // Process files in batches to prevent CPU overload with many large files
      for (let i = 0; i < files.length; i += BATCH_SIZE) {
        if (cancelled) return; // Check cancellation between batches

        const batch = files.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async (file) => {
          const enrichedFile = file as FileWithDuration;
          try {
            const [mediaInfo, thumbnail, fingerprint] = await Promise.all([
              getMediaInfo(file),
              // 256px preview — large enough to read at card size, small enough
              // to hold ~10–30KB per file in memory during a batch upload.
              getVideoThumbnail(file, 0.7, 0, 256),
              // Retries internally and resolves to undefined on failure (never
              // rejects), so a fingerprint error can't also drop the thumbnail/
              // duration or exclude the file.
              fingerprintWithRetry(file),
            ]);
            if (mediaInfo?.durationSeconds !== undefined) {
              enrichedFile.duration = mediaInfo.durationSeconds;
            }
            if (thumbnail !== undefined) {
              enrichedFile.thumbnail = thumbnail;
            } else {
              // Image files: getVideoThumbnail returns undefined, so generate the
              // 256px preview directly from the source image. Mirrors the video path.
              const imagePreview = await generateLQIP(file, 256);
              if (imagePreview !== undefined) {
                enrichedFile.thumbnail = imagePreview;
              }
            }
            if (mediaInfo?.width !== undefined && mediaInfo.height !== undefined) {
              enrichedFile.width = mediaInfo.width;
              enrichedFile.height = mediaInfo.height;
            }

            // Generate LQIP: from video thumbnail if available, otherwise from image file directly.
            // Used as the blurred placeholder behind the main image once the CDN thumbnail is ready.
            const lqip = thumbnail ? await generateLQIP(thumbnail) : await getImageThumbnail(file);
            if (lqip !== undefined) {
              enrichedFile.lqip = lqip;
            }

            return { enrichedFile, fingerprint };
          } catch (error) {
            // Individual file error - will be logged only if batch succeeds
            return { enrichedFile, fingerprint: undefined, error };
          }
        });

        // Batch-level error handling
        try {
          const batchResults = await Promise.all(batchPromises);
          if (cancelled) return; // Check cancellation after batch completes

          // Log individual errors only if batch as a whole succeeded, and mark
          // every file in the batch as extracted (even ones that errored) so
          // their rows leave the skeleton state and stay uploadable.
          batchResults.forEach(({ enrichedFile, error }) => {
            enrichedFile.metadataExtracted = true;
            if (error) {
              console.warn(
                `Failed to extract metadata for file "${enrichedFile.name}":`,
                error instanceof Error ? error.message : error,
              );
            }
          });

          // Update state incrementally after each batch to prevent memory spikes
          // This allows garbage collection to run between batches for large file sets
          setFilesWithFingerprints((prev) => {
            const updated = new Map(prev);
            batchResults.forEach(({ enrichedFile, fingerprint }) => {
              if (fingerprint) {
                updated.set(enrichedFile, fingerprint);
              }
            });
            return updated;
          });

          allEnrichedFiles.push(...batchResults.map((r) => r.enrichedFile));

          // Bump the array ref after each batch so consumers re-render and pick
          // up the metadata just mutated onto these files (skeletons → real
          // thumbnails/durations), rather than waiting for the whole set.
          setFilesWithMetadata([...files]);
        } catch (error) {
          // Entire batch failed - log batch error instead of individual errors
          console.error(
            `Batch processing failed for ${batch.length} files (batch ${i / BATCH_SIZE + 1}):`,
            error instanceof Error ? error.message : error,
          );
          // Add files with undefined fingerprints so processing continues. Mark
          // them extracted so their rows resolve and stay uploadable.
          const fallbackResults = batch.map((file) => {
            const enriched = file as FileWithDuration;
            enriched.metadataExtracted = true;
            return enriched;
          });
          allEnrichedFiles.push(...fallbackResults);

          // Bump the array ref here too (mirrors the success path) so these
          // files leave the skeleton state right away rather than lingering
          // until the next successful batch or final completion.
          setFilesWithMetadata([...files]);
        }
      }

      if (cancelled) return; // Final check before state updates

      setFilesWithMetadata(allEnrichedFiles);
      setIsFingerprintingComplete(true);
    };

    // Defer the heavy extraction until AFTER the selection modal has mounted
    // and painted. Starting synchronously here would decode the first batch's
    // thumbnails (spinning up several <video> elements) on the same frame the
    // modal is trying to appear, adding a perceptible pause before it shows.
    // Double rAF = "after the next paint"; the rows already render skeletons in
    // the meantime. (If the tab is backgrounded the rAFs pause, which is fine —
    // extraction simply resumes on refocus.)
    const rafIds: number[] = [];
    rafIds.push(
      requestAnimationFrame(() => {
        rafIds.push(
          requestAnimationFrame(() => {
            if (!cancelled) enrichFilesWithMetadata();
          }),
        );
      }),
    );

    return () => {
      cancelled = true;
      rafIds.forEach((id) => cancelAnimationFrame(id));
      setIsFingerprintingComplete(false);
      setFilesWithFingerprints(new Map());
    };
  }, [files]);

  return { filesWithMetadata, filesWithFingerprints, isFingerprintingComplete };
}
